import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import LeaseDealCard from "@/components/LeaseDealCard";
import { api } from "@/lib/api";
import { MapPin, Sparkles } from "lucide-react";

export default function DealsNearMe() {
  const [zip, setZip] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = async (e) => {
    e?.preventDefault?.();
    if (zip.length < 5) return;
    setBusy(true);
    try {
      const { data } = await api.get("/deals-near-me", { params: { zip } });
      setResult(data);
    } finally { setBusy(false); }
  };

  useEffect(() => {
    const cached = localStorage.getItem("user_zip");
    if (cached) { setZip(cached); }
  }, []);

  useEffect(() => {
    if (zip.length === 5 && /^\d+$/.test(zip)) {
      localStorage.setItem("user_zip", zip);
    }
  }, [zip]);

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12 md:py-16" data-testid="deals-near-me-page">
      <div className="flex items-center gap-2 mb-3"><MapPin className="w-4 h-4 text-silver" /><span className="label-mono">Geo Lease Finder</span></div>
      <h1 className="font-display text-4xl md:text-6xl font-light tracking-tighter mb-5">Deals near you.</h1>
      <p className="text-gray-400 mb-10 max-w-2xl">Enter your ZIP. We match state-specific incentives and nationwide offers, sorted by lowest payment.</p>

      <form onSubmit={search} className="glass rounded-2xl p-5 md:p-7 mb-10 flex flex-col sm:flex-row gap-3 max-w-2xl">
        <Input
          value={zip}
          onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
          placeholder="Enter ZIP (e.g. 28202)"
          inputMode="numeric"
          maxLength={5}
          className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-full text-white px-5 flex-1"
          data-testid="zip-input"
        />
        <button type="submit" disabled={busy || zip.length < 5} className="px-7 py-3 rounded-full silver-gradient text-[#050507] font-bold disabled:opacity-50" data-testid="zip-search">
          {busy ? "Searching…" : "Find Deals"}
        </button>
      </form>

      {result && (
        <>
          <div className="flex items-center gap-2 mb-6">
            <Sparkles className="w-4 h-4 text-silver" />
            <p className="text-sm text-gray-300">
              Showing offers for {result.state_name || "your area"}
              {result.state ? ` (${result.state})` : ""} — {result.deals.length} deals
            </p>
          </div>
          {result.deals.length === 0 ? (
            <div className="border border-dashed border-white/10 rounded-2xl p-10 text-center text-gray-400">
              No matches for this region yet. Try a different ZIP or browse all deals.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="near-me-deals">
              {result.deals.map((d) => <LeaseDealCard key={d.id} deal={d} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
